import { Country, Language } from '@/types';
import { client } from '@/client';
import { CodeLoginRequest } from '@/auth';

export enum CallToActionLocation {
  NEW_AUTH_REQUEST = 'NEW_AUTH_REQUEST',
  NEW_SIGN_REQUEST = 'NEW_SIGN_REQUEST',
  LIST_REQUESTS = 'LIST_REQUESTS',
  LOGIN = 'LOGIN',
  AUTH = 'AUTH',
  SIGN = 'SIGN',
}

export type PrivateAppConfig = {
  callToActions: {
    title: string;
    body: string;
    buttonText: string;
    buttonLink: string;
    language: Language;
    location: CallToActionLocation;
  }[];
};

export type PrivateUser = {
  name: string;
  email?: string;
  checksLeft: number;
  termsOfServiceAccepted: boolean;
  hasValidatedEmail: boolean;
};

export type InitCheckoutRequest = {
  amount: number;
  country: Country;
};

export type CreateAuthRequest = {
  // At least one of email and phone must be set
  email?: string;
  phone?: string;
  country: Country;
  name: string;
  subject: string;
};

export type AuthRequest = {
  id: string;
  subject: string;
  status: 'COMPLETE' | 'PENDING';
  type: 'AUTH' | 'SIGN';
  requestedAt: number;
  givenName: string;
  registeredName?: string;
  authenticatedAt?: number;
  authenticatedUsing?: 'BANKID' | 'BANKID_NO' | 'MITID' | 'FTN';
};

export type AuthStatus = {
  authenticated: boolean;
  subject: string;
  recipientName: string;
  requestorName: string;
};

export type PrivateAppAuthResultResponse =
  | {
      status: 'IN_PROGRESS';
      ref: string;
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
    }
  | {
      status: 'FAILED';
      ref: string;
      error: string;
    };

export type CreateSignRequest = {
  // At least one of email and phone must be set
  email?: string;
  phone?: string;
  country: Country;
  name: string;
  subject: string;
  text: string;
};

export type SignStatus = {
  authenticated: boolean;
  subject: string;
  signText: string;
  hash: string;
  recipientName: string;
  requestorName: string;
};

export type PrivateAppSignResultResponse =
  | {
      status: 'IN_PROGRESS';
      ref: string;
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
    }
  | {
      status: 'FAILED';
      ref: string;
      error: string;
    };

export type PrivateAppLoginResponse =
  | {
      status: 'IN_PROGRESS';
      ref: string;
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
      token: string;
      user: {
        name: string;
        email: string | null;
        credits: number;
        termsOfServiceAccepted: boolean;
        hasValidatedEmail: boolean;
      };
    }
  | {
      status: 'FAILED';
      ref: string;
      error: string;
    };

export async function getPrivateConfig(
  signal?: AbortSignal,
): Promise<PrivateAppConfig> {
  return client
    .get('/private/config', { signal })
    .then((response) => response.data);
}

export async function getOwnPrivateUser(
  signal?: AbortSignal,
): Promise<PrivateUser> {
  return client
    .get('/private/user/me', { signal })
    .then((response) => response.data);
}

export async function updateOwnPrivateUserEmail(
  email: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .put('/private/user/email', email, {
      headers: {
        'Content-Type': 'text/plain',
      },
      signal,
    })
    .then((response) => response.data);
}

export async function acceptPrivateAppTermsOfServices(
  signal?: AbortSignal,
): Promise<void> {
  return client
    .put('/private/user/termsofservice', null, { signal })
    .then((response) => response.data);
}

export async function deleteOwnPrivateUser(
  signal?: AbortSignal,
): Promise<void> {
  return client
    .delete('/private/user/me', { signal })
    .then((response) => response.data);
}

export async function initCheckout(
  request: InitCheckoutRequest,
  signal?: AbortSignal,
): Promise<string> {
  return client
    .post('/private/checkout', request, { signal })
    .then((response) => response.data);
}

export async function createAuthRequest(
  request: CreateAuthRequest,
  signal?: AbortSignal,
): Promise<string> {
  return client
    .post('/private/auth-request', request, { signal })
    .then((response) => response.data);
}

export async function deleteAuthRequest(
  id: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .delete(`/private/auth-request/${id}`, { signal })
    .then((response) => response.data);
}

export async function createSignRequest(
  request: CreateSignRequest,
  signal?: AbortSignal,
): Promise<string> {
  return client
    .post('/private/sign-request', request, { signal })
    .then((response) => response.data);
}

export async function deleteSignRequest(
  id: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .delete(`/private/sign-request/${id}`, { signal })
    .then((response) => response.data);
}

export async function getAuthRequestSignature(
  id: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get(`/private/sign-request/${id}/signature`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function getAuthRequests(
  signal?: AbortSignal,
): Promise<AuthRequest[]> {
  return client
    .get(`/private/auth-request`, { signal })
    .then((response) => response.data);
}

export async function swedenPrivateLogin(
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .post(`/private/sweden/login`, {}, { signal })
    .then((response) => response.data);
}

export async function getPrivateLoginResult(
  id: string,
  signal?: AbortSignal,
): Promise<PrivateAppLoginResponse> {
  return client
    .get(`/private/sweden/login/${encodeURIComponent(id)}`, { signal })
    .then((response) => response.data);
}

export async function initPrivateNorwayLogin(
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get('/private/norway/login-url', { signal })
    .then((response) => response.data);
}

export async function initPrivateDenmarkLogin(
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get('/private/denmark/login-url', { signal })
    .then((response) => response.data);
}

export async function initPrivateFinlandLogin(
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get('/private/finland/login-url', { signal })
    .then((response) => response.data);
}

export async function norwayPrivateLogin(
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginResponse, { status: 'COMPLETED' }>> {
  return client
    .post('/private/norway/login', request, { signal })
    .then((response) => response.data);
}

export async function denmarkPrivateLogin(
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginResponse, { status: 'COMPLETED' }>> {
  return client
    .post('/private/denmark/login', request, { signal })
    .then((response) => response.data);
}

export async function finlandPrivateLogin(
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginResponse, { status: 'COMPLETED' }>> {
  return client
    .post('/private/finland/login', request, { signal })
    .then((response) => response.data);
}

export async function getAuthStatus(
  id: string,
  signal?: AbortSignal,
): Promise<AuthStatus> {
  return client
    .get(`/private/auth/${id}`, { signal })
    .then((response) => response.data);
}

export async function swedenPrivateAuth(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppAuthResultResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .post(`/private/sweden/auth/${encodeURIComponent(id)}`, {}, { signal })
    .then((response) => response.data);
}

export async function getPrivateAuthResult(
  id: string,
  signal?: AbortSignal,
): Promise<PrivateAppAuthResultResponse> {
  return client
    .get(`/private/sweden/auth/${encodeURIComponent(id)}`, { signal })
    .then((response) => response.data);
}

export async function initPrivateNorwayAuth(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppAuthResultResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get(`/private/auth/norway/${encodeURIComponent(id)}/url`, {
      signal,
    })
    .then((response) => response.data);
}

export async function initPrivateDenmarkAuth(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppAuthResultResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get(`/private/auth/denmark/${encodeURIComponent(id)}/url`, {
      signal,
    })
    .then((response) => response.data);
}

export async function initPrivateFinlandAuth(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppAuthResultResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get(`/private/auth/finland/${encodeURIComponent(id)}/url`, {
      signal,
    })
    .then((response) => response.data);
}

export async function norwayPrivateAuth(
  id: string,
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/private/auth/norway/${encodeURIComponent(id)}`, request, { signal })
    .then((response) => response.data);
}

export async function denmarkPrivateAuth(
  id: string,
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/private/auth/denmark/${encodeURIComponent(id)}`, request, {
      signal,
    })
    .then((response) => response.data);
}

export async function finlandPrivateAuth(
  id: string,
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/private/auth/finland/${encodeURIComponent(id)}`, request, {
      signal,
    })
    .then((response) => response.data);
}

export async function getSignStatus(
  id: string,
  signal?: AbortSignal,
): Promise<SignStatus> {
  return client
    .get(`/private/sign/${id}`, { signal })
    .then((response) => response.data);
}

export async function swedenPrivateSign(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppSignResultResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .post(`/private/sweden/sign/${encodeURIComponent(id)}`, {}, { signal })
    .then((response) => response.data);
}

export async function getPrivateSignResult(
  id: string,
  signal?: AbortSignal,
): Promise<PrivateAppSignResultResponse> {
  return client
    .get(`/private/sweden/sign/${encodeURIComponent(id)}`, { signal })
    .then((response) => response.data);
}

export async function initPrivateNorwaySign(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppSignResultResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get(`/private/sign/norway/${encodeURIComponent(id)}/url`, {
      signal,
    })
    .then((response) => response.data);
}

export async function initPrivateDenmarkSign(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppSignResultResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get(`/private/sign/denmark/${encodeURIComponent(id)}/url`, {
      signal,
    })
    .then((response) => response.data);
}

export async function initPrivateFinlandSign(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppSignResultResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .get(`/private/sign/finland/${encodeURIComponent(id)}/url`, {
      signal,
    })
    .then((response) => response.data);
}

export async function norwayPrivateSign(
  id: string,
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/private/sign/norway/${encodeURIComponent(id)}`, request, { signal })
    .then((response) => response.data);
}

export async function denmarkPrivateSign(
  id: string,
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/private/sign/denmark/${encodeURIComponent(id)}`, request, {
      signal,
    })
    .then((response) => response.data);
}

export async function finlandPrivateSign(
  id: string,
  request: CodeLoginRequest,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/private/sign/finland/${encodeURIComponent(id)}`, request, {
      signal,
    })
    .then((response) => response.data);
}

export async function validatePrivateUserEmail(
  code: string,
  signal?: AbortSignal,
): Promise<void> {
  return client.post('/private/email/validate', null, {
    params: { code },
    signal,
  });
}
